import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Table, Modal, Input, Space, Typography } from 'antd';
import { SelectOutlined } from '@ant-design/icons';
import { transactionFields } from '../../constants/transaction';

const { TextArea } = Input;
const { Title } = Typography;

const XML_FIELDS = [
  { key: 'requestXml', labelKey: 'aibTransactionLog.requestXml' },
  { key: 'requestEnvelope', labelKey: 'aibTransactionLog.requestEnvelope' },
  { key: 'responseXml', labelKey: 'aibTransactionLog.responseXml' },
  { key: 'responseEnvelope', labelKey: 'aibTransactionLog.responseEnvelope' },
];

const COLUMN_KEYS = [
  'number',
  'sender',
  'receiver',
  'transactionDirection',
  'transactionStatus',
  'created',
  'actions',
];

function LogDetailsModal({ open, onClose, fields, t }) {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="70%"
      title={
        <Title
          level={4}
        >{`${t('aibTransactionLog.detailsHeader')} ${fields?.number ?? ''}`}</Title>
      }
      footer={null}
      centered
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {XML_FIELDS.map(({ key, labelKey }) => (
          <div key={key}>
            <div style={{ marginBottom: 8 }}>{t(labelKey)}</div>
            <TextArea
              value={fields?.[key] ?? ''}
              readOnly
              rows={10}
              style={{
                resize: 'both',
                minHeight: 120,
                maxHeight: 600,
                fontFamily: 'monospace',
                overflow: 'auto',
              }}
            />
          </div>
        ))}
      </Space>
    </Modal>
  );
}

LogDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fields: PropTypes.shape({
    number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    requestXml: PropTypes.string,
    requestEnvelope: PropTypes.string,
    responseXml: PropTypes.string,
    responseEnvelope: PropTypes.string,
  }),
  t: PropTypes.func.isRequired,
};

LogDetailsModal.defaultProps = {
  fields: {
    number: '',
    requestXml: '',
    requestEnvelope: '',
    responseXml: '',
    responseEnvelope: '',
  },
};

const AibTransactionLog = ({ info, t }) => {
  const dataSource =
    info[transactionFields.EXTERNAL_TRANSACTION_AIB_LOG_LIST] ?? [];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState({
    number: '',
    requestXml: '',
    requestEnvelope: '',
    responseXml: '',
    responseEnvelope: '',
  });

  const openLogDetails = useCallback((record) => {
    setSelected({
      number: record?.number ?? '',
      requestXml: record?.requestXml ?? '',
      requestEnvelope: record?.requestEnvelope ?? '',
      responseXml: record?.responseXml ?? '',
      responseEnvelope: record?.responseEnvelope ?? '',
    });
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const actionsRenderer = useCallback(
    (_, record) => (
      <Button
        shape="circle"
        icon={<SelectOutlined />}
        size="small"
        type="primary"
        title={t('table.viewBtn')}
        onClick={() => openLogDetails(record)}
      />
    ),
    [openLogDetails, t]
  );

  const directionRenderer = useCallback(
    (value) => {
      if (value == null) return '';
      return t(`aibTransactionLog.direction.${value}`, {
        defaultValue: String(value),
      });
    },
    [t]
  );

  const columns = useMemo(() => {
    const renderers = {
      actions: actionsRenderer,
      transactionDirection: directionRenderer,
    };

    return COLUMN_KEYS.map((key) => ({
      title: t(`aibTransactionLog.${key}`),
      dataIndex: key,
      key,
      ...(renderers[key] ? { render: renderers[key] } : {}),
    }));
  }, [actionsRenderer, directionRenderer, t]);

  return (
    <>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        rowKey={(row) =>
          row?.id ?? row?.number ?? `${row?.sender}-${row?.created}`
        }
      />
      <LogDetailsModal
        open={isModalOpen}
        onClose={closeModal}
        fields={selected}
        t={t}
      />
    </>
  );
};

AibTransactionLog.propTypes = {
  info: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

export default AibTransactionLog;
